import * as _ from 'lodash-es';
import { PlanTier, TopicPrivacy } from '../../graphql/generated/graphql';
import { isFeatureEnabled } from '../../utils/features';
import { getPrivacyLabel } from '../getTopicPrivacyInfo';

const PAID_PRIVACIES: TopicPrivacy[] = [
  ...(isFeatureEnabled('public_posts_topics') ? [] : [TopicPrivacy.Public]),
  TopicPrivacy.Private,
  TopicPrivacy.Secret,
];

export const isLimitedByPlan = (privacy: TopicPrivacy, planTier?: PlanTier) => {
  return planTier === PlanTier.Free && PAID_PRIVACIES.includes(privacy);
};

const getFreePlanError = (privacy: TopicPrivacy, planTier?: PlanTier) => {
  if (planTier === PlanTier.Free && PAID_PRIVACIES.includes(privacy)) {
    return `${_.capitalize(privacy)} topics\nare not available\non your plan.`;
  }

  return null;
};

export const getSubtopicPrivacyError = (
  privacy: TopicPrivacy,
  parentPrivacy?: TopicPrivacy | null,
) => {
  switch (parentPrivacy) {
    case undefined:
    case null:
    case TopicPrivacy.Open:
      return null;
    case TopicPrivacy.Public:
    case TopicPrivacy.Secret: {
      const label = getPrivacyLabel(parentPrivacy);
      return parentPrivacy === privacy
        ? null
        : `${label} topics can only have ${label} subtopics`;
    }
    case TopicPrivacy.Private:
      return privacy !== TopicPrivacy.Private && privacy !== TopicPrivacy.Secret
        ? 'Private topics can\nonly have Private or\nSecret subtopics'
        : null;
    default:
      return null;
  }
};

function getPublicPermissionError(
  privacy: TopicPrivacy,
  isPermittedToSetTopicToPublic: boolean,
): string | null {
  return privacy === TopicPrivacy.Public && !isPermittedToSetTopicToPublic
    ? 'Only admins can\ncreate public topics'
    : null;
}

function getTopicPrivacyError(
  privacy: TopicPrivacy,
  {
    parentPrivacy,
    planTier,
    isPermittedToSetTopicToPublic = true,
  }: {
    parentPrivacy?: TopicPrivacy | null;
    planTier?: PlanTier;
    isPermittedToSetTopicToPublic?: boolean;
  },
): string | null {
  return (
    getFreePlanError(privacy, planTier) ||
    getSubtopicPrivacyError(privacy, parentPrivacy) ||
    getPublicPermissionError(privacy, isPermittedToSetTopicToPublic) ||
    null
  );
}

export default getTopicPrivacyError;

export function getParentPrivacyError(
  parentPrivacy: TopicPrivacy | null,
  {
    planTier,
    isPermittedToSetTopicToPublic = true,
  }: {
    planTier?: PlanTier;
    isPermittedToSetTopicToPublic?: boolean;
  },
): string | null {
  if (!parentPrivacy) {
    return null;
  }

  if (getFreePlanError(parentPrivacy, planTier)) {
    return 'This topic can only\nbe organized under an\nopen or public topic.';
  }

  if (getPublicPermissionError(parentPrivacy, isPermittedToSetTopicToPublic)) {
    return 'Only admins can\norganize into\npublic topics.';
  }

  return null;
}
